import { useState, useMemo } from 'react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Box, Tooltip, Switch, Select, MenuItem, FormControl, IconButton, CircularProgress } from '@mui/material';
import {
  DataTable,
  PageHeader,
  FilterTabs,
  StatusChip,
  MetricCard,
  EmptyState,
  Column,
} from '@/components/shared';
import { SeoAgent, SeoAgentMode, SeoAgentTrack } from '@/types/api/seo';

type Stats = {
  totalRuns: number;
  totalChanges: number;
  totalTokens: number;
  totalCost: number;
  errorRate: number;
};

type Props = {
  agents: SeoAgent[];
  stats: Stats | null;
  isLoading: boolean;
  onToggleEnabled: (agentId: string, enabled: boolean) => Promise<void>;
  onUpdateMode: (agentId: string, mode: SeoAgentMode) => Promise<void>;
  onRunAgent: (agentId: string) => Promise<void>;
  onInitialize: () => Promise<void>;
  isInitializing: boolean;
  runningAgentId: string | null;
};

const trackStatusMap: Record<SeoAgentTrack, { label: string; color: string }> = {
  blog: { label: 'Blog', color: '#0D9D58' },
  service: { label: 'Service', color: '#054AD9' },
  both: { label: 'Both', color: '#7C3AED' },
};

const runStatusMap: Record<string, { label: string; color: string }> = {
  completed: { label: 'Completed', color: '#0D9D58' },
  running: { label: 'Running', color: '#054AD9' },
  error: { label: 'Error', color: '#EA4335' },
  skipped: { label: 'Skipped', color: '#71717A' },
};

const formatNumber = (n: number): string => n.toLocaleString('en-US');

const formatCurrency = (n: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const relativeTime = (dateStr: string | null): string => {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const SeoAutomationComponent = function ({
  agents,
  stats,
  isLoading,
  onToggleEnabled,
  onUpdateMode,
  onRunAgent,
  onInitialize,
  isInitializing,
  runningAgentId,
}: Props): JSX.Element {
  const [activeTrack, setActiveTrack] = useState<string>('all');

  const filteredAgents = useMemo(() => {
    if (activeTrack === 'all') return agents;
    return agents.filter((a) => a.track === activeTrack || a.track === 'both');
  }, [agents, activeTrack]);

  const blogCount = useMemo(
    () => agents.filter((a) => a.track === 'blog' || a.track === 'both').length,
    [agents]
  );
  const serviceCount = useMemo(
    () => agents.filter((a) => a.track === 'service' || a.track === 'both').length,
    [agents]
  );

  const columns: Column<SeoAgent>[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Agent',
        render: (row) => (
          <Tooltip title={row.description ?? ''} placement="top" arrow>
            <Box
              sx={{
                fontWeight: 500,
                fontSize: 14,
                cursor: 'default',
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.name}
            </Box>
          </Tooltip>
        ),
        width: 220,
      },
      {
        id: 'track',
        label: 'Track',
        align: 'center' as const,
        render: (row) => <StatusChip status={row.track} statusMap={trackStatusMap} />,
        width: 100,
      },
      {
        id: 'mode',
        label: 'Mode',
        align: 'center' as const,
        render: (row) => (
          <FormControl size="small" variant="standard">
            <Select
              value={row.mode}
              onChange={(e) => onUpdateMode(row.id, e.target.value as SeoAgentMode)}
              disableUnderline
              sx={{
                fontSize: 12,
                fontWeight: 500,
                minWidth: 90,
                '& .MuiSelect-select': {
                  padding: '4px 24px 4px 8px',
                  borderRadius: '8px',
                  backgroundColor:
                    // eslint-disable-next-line no-nested-ternary
                    row.mode === 'auto'
                      ? '#0D9D5810'
                      : row.mode === 'shadow'
                      ? '#F4B40010'
                      : '#71717A10',
                },
              }}
            >
              <MenuItem value="shadow" sx={{ fontSize: 12 }}>
                Shadow
              </MenuItem>
              <MenuItem value="auto" sx={{ fontSize: 12 }}>
                Auto
              </MenuItem>
              <MenuItem value="disabled" sx={{ fontSize: 12 }}>
                Disabled
              </MenuItem>
            </Select>
          </FormControl>
        ),
        width: 120,
      },
      {
        id: 'schedule',
        label: 'Schedule',
        render: (row) => (
          <Box sx={{ fontSize: 13, color: '#4A4B4E' }}>{row.scheduleLabel ?? '—'}</Box>
        ),
        width: 140,
      },
      {
        id: 'lastRun',
        label: 'Last Run',
        render: (row) => (
          <Box sx={{ fontSize: 13, color: '#71717A' }}>{relativeTime(row.lastRunAt)}</Box>
        ),
        width: 100,
      },
      {
        id: 'status',
        label: 'Status',
        align: 'center' as const,
        render: (row) =>
          row.lastRunStatus ? (
            <StatusChip status={row.lastRunStatus} statusMap={runStatusMap} />
          ) : (
            <Box sx={{ fontSize: 13, color: '#71717A' }}>—</Box>
          ),
        width: 100,
      },
      {
        id: 'enabled',
        label: 'Enabled',
        align: 'center' as const,
        render: (row) => (
          <Switch
            checked={row.enabled}
            onChange={(e) => onToggleEnabled(row.id, e.target.checked)}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#054AD9',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#054AD9',
              },
            }}
          />
        ),
        width: 80,
      },
      {
        id: 'run',
        label: '',
        align: 'center' as const,
        render: (row) => (
          <Tooltip title={row.enabled ? 'Run now' : 'Enable agent first'}>
            <span>
              <IconButton
                size="small"
                disabled={!row.enabled || runningAgentId === row.id}
                onClick={() => onRunAgent(row.id)}
                sx={{
                  color: '#054AD9',
                  '&:hover': { backgroundColor: '#054AD910' },
                  '&.Mui-disabled': { color: '#D1D5DB' },
                }}
              >
                {runningAgentId === row.id ? (
                  <CircularProgress size={16} sx={{ color: '#054AD9' }} />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ),
        width: 50,
      },
    ],
    [onToggleEnabled, onUpdateMode, onRunAgent, runningAgentId]
  );

  if (!isLoading && agents.length === 0) {
    return (
      <>
        <PageHeader title="Automation" />
        <EmptyState
          icon={SmartToyIcon}
          title="No agents configured"
          description="Initialize the default set of SEO agents to start automating optimizations for this project."
          action={{
            label: isInitializing ? 'Initializing...' : 'Initialize Agents',
            onClick: onInitialize,
          }}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Automation" />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 2,
          mb: 3,
          '@media (max-width: 767px)': {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
        }}
      >
        <MetricCard
          label="Total Runs (30d)"
          value={stats ? formatNumber(stats.totalRuns) : '—'}
          isLoading={isLoading}
        />
        <MetricCard
          label="Changes Applied"
          value={stats ? formatNumber(stats.totalChanges) : '—'}
          color="#0D9D58"
          isLoading={isLoading}
        />
        <MetricCard
          label="Tokens Used"
          value={stats ? formatNumber(stats.totalTokens) : '—'}
          color="#7C3AED"
          isLoading={isLoading}
          subtitle={stats ? formatCurrency(stats.totalCost) : undefined}
        />
        <MetricCard
          label="Error Rate"
          value={stats ? `${stats.errorRate}%` : '—'}
          color={stats && stats.errorRate > 10 ? '#EA4335' : '#71717A'}
          isLoading={isLoading}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <FilterTabs
          tabs={[
            { key: 'all', label: 'All', count: agents.length },
            { key: 'blog', label: 'Blog', count: blogCount },
            { key: 'service', label: 'Service', count: serviceCount },
          ]}
          activeKey={activeTrack}
          onChange={setActiveTrack}
        />
      </Box>

      <DataTable
        columns={columns}
        data={filteredAgents}
        isLoading={isLoading}
        rowKey={(r) => r.id}
        searchPlaceholder="Search agents..."
        searchFields={(r) => `${r.name} ${r.description ?? ''} ${r.track}`}
        emptyMessage="No agents match this filter."
        emptyIcon={SmartToyIcon}
      />
    </>
  );
};

export default SeoAutomationComponent;
